import { type Ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';

import useAuth from '@/composables/useAuth';
import appConfig from '@/config';
import { useSalesEdgeIntegrationStore } from '@/stores/SalesEdgeIntegrationStore';

type StatusBadgeType = 'success' | 'warning' | 'error';

export default function useSalesEdgeIntegrationStatus(
  isHubspotCopilot: Ref<boolean>,
) {
  const store = useSalesEdgeIntegrationStore();
  const { currentOrg } = useAuth();

  watch(
    [isHubspotCopilot, () => currentOrg.value?.uuid],
    ([isHubspot, orgUuid]) => {
      if (isHubspot && orgUuid) store.fetchStatus();
    },
    { immediate: true },
  );

  function handleWindowFocus() {
    if (!isHubspotCopilot.value) return;
    if (store.consumeReauthorizationInFlight()) store.fetchStatus();
  }
  onMounted(() => window.addEventListener('focus', handleWindowFocus));
  onBeforeUnmount(() => window.removeEventListener('focus', handleWindowFocus));

  function triggerReauthorization() {
    store.flagReauthorizationInFlight();
    window.open(
      `${appConfig.salesEdgeRedirectUrl}/oauth/hubspot-integration`,
      '_blank',
    );
  }

  const statusType = computed<StatusBadgeType>(() => {
    if (store.isActive) return 'success';
    if (store.isSetupIncomplete) return 'error';
    return 'warning';
  });

  const statusText = computed(() => {
    if (store.isActive) return 'Active';
    if (store.isSetupIncomplete) return 'Setup incomplete';
    if (store.hasNoAccess) return 'No access';
    if (store.hasError) return 'Status unknown';
    return 'Checking…';
  });

  return {
    statusType,
    statusText,
    isActive: computed(() => store.isActive),
    isSetupIncomplete: computed(() => store.isSetupIncomplete),
    hasError: computed(() => store.hasError),
    hasNoAccess: computed(() => store.hasNoAccess),
    lastSyncAt: computed(() => store.lastSyncAt),
    triggerReauthorization,
  };
}
